import { Close, DarkMode, LightMode, Reorder, Search } from '@mui/icons-material'; // Import Search and Close icons
import { Box, Button, Divider, IconButton, Link, Popover, TextField, Typography, useMediaQuery } from '@mui/material';
import axios from 'axios';
import React from 'react';
import { useQueryClient } from 'react-query';
import { useDispatch, useSelector } from 'react-redux';
import { Link as RouterLink, useNavigate } from 'react-router-dom';

import { unauthenticate } from '../features/auth/auth.slice';
import { clearUserDetails } from '../features/users/users.slice';

import { toggleSidebar } from '../features/sidebar/sidebar.slice';
import { setTheme } from '../features/theme/theme.slice';

import '../assets/css/styles.navbar.scss';
import ProfileAvatar from './ProfileAvatar';

const Navbar = () => {
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [showSearch, setShowSearch] = React.useState(false); // State for search field visibility
  const open = Boolean(anchorEl);

  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const user = useSelector((state) => state.user);
  const color = useSelector((state) => state.theme);

  const prefersDarkMode = useMediaQuery('(prefers-color-scheme: dark)');

  const showMenuHandler = (e) => {
    setAnchorEl(e.currentTarget);
  };
  const closeMenuHandler = () => {
    setAnchorEl(null);
  };
  const toggleSidebarHandler = () => {
    dispatch(toggleSidebar());
  };
  const logoutHandler = async () => {
    try {
      await axios.post(`/api/logout`);

      window.localStorage.removeItem('token');
      window.localStorage.removeItem('user');
      window.localStorage.removeItem('is_my_request');
      window.localStorage.removeItem('is_my_approve');
      window.localStorage.removeItem('is_confidential');
      window.localStorage.removeItem('is_mc');

      dispatch(unauthenticate());
      dispatch(clearUserDetails());
      queryClient.clear();

      //ORIGINAL LOGOUT FLOW
      // navigate('/', {
      //   replace: true,
      // });

      // LOGOUT REDIRECT TO ONE RDF
      const oneRdfWindow = window.open('', 'OneRDF_Portal');
      try {
        if (oneRdfWindow.location.href === 'about:blank' || oneRdfWindow.location.href === '') {
          oneRdfWindow.close();
          window.location.href = 'https://one.rdfmis.com/login';
        } else {
          window.close();
        }
      } catch (error) {
        window.close();
      }
    } catch (error) {
      console.log(error.response);
    }
  };


  const toggleColorHandler = () => {
    if (color === 'system') {
      if (prefersDarkMode) {
        window.localStorage.setItem('color', 'light');
        dispatch(setTheme('light'));
      } else {
        window.localStorage.setItem('color', 'dark');
        dispatch(setTheme('dark'));
      }
    } else if (color === 'dark') {
      window.localStorage.setItem('color', 'light');
      dispatch(setTheme('light'));
    } else {
      window.localStorage.setItem('color', 'dark');
      dispatch(setTheme('dark'));
    }
  };
  const systemColorHandler = () => {
    window.localStorage.setItem('color', 'system');
    dispatch(setTheme('system'));
  };

  const toggleSearchHandler = () => {
    setShowSearch((prev) => !prev);
  };

  const searchContainerStyles = {
    display: 'flex',
    alignItems: 'center',
    flexGrow: 1,
    justifyContent: 'flex-end',
    overflow: 'hidden',
    position: 'relative',
  };

  const searchFieldStyles = {
    position: 'absolute',
    right: 0,
    maxWidth: '450px',
    width: showSearch ? '100%' : '0', // Animate width
    opacity: showSearch ? 1 : 0, // Animate opacity
    pointerEvents: showSearch ? 'auto' : 'none', // Enable/disable interaction
    transition: 'width 0.3s ease-out, opacity 0.3s ease-out', // Smooth transition
  };

  return (
    <nav className={'FstoNavbar-root ' + color}>
      <Box className="FstoNavbar-toggle">
        <IconButton onClick={toggleSidebarHandler}>
          <Reorder />
        </IconButton>
      </Box>

      <Box sx={searchContainerStyles}>
        <IconButton onClick={toggleSearchHandler} sx={{ zIndex: 2 }}>
          {showSearch ? <Close /> : <Search />}
        </IconButton>
        <TextField
          size="small"
          placeholder="Globar Search"
          sx={{
            ...searchFieldStyles,
          }}
        />
      </Box>
      <Box className="FstoNavbar-user">
        <>
          <h5>Fresh Morning , {user?.first_name.toLowerCase()}!</h5>
        </>
        <IconButton sx={{ ml: '4px' }} onClick={showMenuHandler}>
          <ProfileAvatar name={`${user?.first_name} ${user?.last_name}`} />
        </IconButton>
      </Box>

      <Popover
        className="FstoPopoverNavbar-root"
        open={open}
        elevation={2}
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        slotProps={{
          paper: { className: 'FstoNavbar-menu' },
        }}
        onClose={closeMenuHandler}
      >
        <Typography
          variant="h6"
          sx={{
            minWidth: 270,
            fontWeight: 700,
            textTransform: 'uppercase',
            lineHeight: 1,
          }}
        >
          {user?.last_name}, {user?.first_name} {user?.middle_name?.charAt(0)}.
        </Typography>

        <Typography
          variant="caption"
          sx={{
            fontWeight: 500,
            textTransform: 'uppercase',
            lineHeight: 1,
          }}
        >
          {user?.role}
        </Typography>

        <Divider sx={{ my: 2 }} />

        {/* <Typography
          variant="subtitle2"
          sx={{
            mb: 1,
            fontWeight: 700,
          }}
        >
          Profile Settings
        </Typography>

        <Typography variant="subtitle1">
          <Link
            color="inherit"
            to="/user/change-password"
            component={RouterLink}
            onClick={closeMenuHandler}
            sx={{
              display: 'block',
              ml: 2,
              textDecoration: 'none',
              '&:hover': {
                textDecoration: 'underline',
              },
            }}
          >
            Change Password
          </Link>
        </Typography>

        <Divider sx={{ my: 2 }} /> */}

        <Typography
          variant="subtitle2"
          sx={{
            mb: 1,
            fontWeight: 700,
          }}
        >
          Theme Settings
        </Typography>

        <Button
          sx={{
            background: 'rgba(0,0,0,0.04)',
            ml: 2,
            mr: 1,
            borderRadius: '25px',
            textTransform: 'capitalize',
          }}
          onClick={systemColorHandler}
          disabled={color === 'system'}
          color="secondary"
        >
          System Color
        </Button>
        <IconButton size="small" onClick={toggleColorHandler}>
          {color === 'system' ? prefersDarkMode ? <LightMode /> : <DarkMode /> : color === 'dark' ? <LightMode /> : <DarkMode />}
        </IconButton>

        <Divider sx={{ my: 2 }} />

        <Typography
          variant="subtitle1"
          sx={{
            ml: 2,
            cursor: 'pointer',
            '&:hover': {
              textDecoration: 'underline',
            },
          }}
          onClick={logoutHandler}
        >
          Log Out
        </Typography>
      </Popover>
    </nav>
  );
};

export default Navbar;
